import React, { useState, useEffect, useCallback } from 'react';
import { Download, Filter, X, Eye, Trash2 } from 'lucide-react';
import api from '../api/client';
import RaceResultsViewer from './RaceResultsViewer';
import './Results.css';

interface ResultFile {
  filename: string;
  date: string;
  time: string;
  type: string;
  size: number;
}

interface ResultsProps {
  serverId: string;
}

const Results: React.FC<ResultsProps> = ({ serverId }) => {
  const [results, setResults] = useState<ResultFile[]>([]);
  const [penaltyCounts, setPenaltyCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [downloading, setDownloading] = useState<string | null>(null);
  const [viewingFile, setViewingFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadResults = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [resultsRes, penaltiesRes] = await Promise.all([
        api.getResults(serverId, typeFilter || undefined),
        api.getPenalties(serverId),
      ]);
      setResults(resultsRes.data.results);
      const counts: Record<string, number> = {};
      (penaltiesRes.data.penalties || []).forEach((penalty: any) => {
        counts[penalty.resultFilename] = (counts[penalty.resultFilename] || 0) + 1;
      });
      setPenaltyCounts(counts);
    } catch (err) {
      console.error('Failed to load results:', err);
      setError('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [serverId, typeFilter]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const handleDownload = async (filename: string) => {
    try {
      setDownloading(filename);
      const res = await api.downloadResult(serverId, filename);
      
      // Create blob download
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download result:', err);
      setError('Failed to download result');
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (date: string, time: string) => {
    try {
      const year = date.substring(0, 4);
      const month = date.substring(4, 6);
      const day = date.substring(6, 8);
      const hour = time.substring(0, 2);
      const minute = time.substring(2, 4);
      const second = time.substring(4, 6);
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    } catch {
      return `${date} ${time}`;
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const typeOptions = ['practice', 'qualify', 'warmup', 'race'];

  const handleDeleteResult = async (filename: string) => {
    const confirmed = window.confirm(
      `Delete all imported database records for ${filename}? This will remove the session and related rows from the database.`
    );
    if (!confirmed) {
      return;
    }

    const deleteFile = window.confirm(
      'Also delete the JSON file from disk so it will not be re-imported?'
    );

    try {
      setLoading(true);
      setError(null);
      await api.deleteResultFile(serverId, filename, deleteFile);
      if (viewingFile === filename) {
        setViewingFile(null);
      }
      await loadResults();
    } catch (err) {
      console.error('Failed to delete result:', err);
      setError('Failed to delete result');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="results-container">
      <div className="results-header">
        <h3>Results</h3>
        <div className="results-controls">
          <div className="filter-group">
            <Filter size={16} />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="type-filter"
            >
              <option value="">All Types</option>
              {typeOptions.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
            {typeFilter && (
              <button
                className="clear-filter"
                onClick={() => setTypeFilter('')}
                title="Clear filter"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <button onClick={loadResults} className="refresh-button" disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading results...</div>
      ) : results.length === 0 ? (
        <div className="no-results">
          {typeFilter ? `No results found for type: ${typeFilter}` : 'No results found'}
        </div>
      ) : (
        <div className="results-table-wrapper">
          <table className="results-table">
            <thead>
              <tr>
                <th className="col-date">Date & Time</th>
                <th className="col-type">Type</th>
                <th className="col-size">Size</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result) => (
                <tr key={result.filename} className="result-row">
                  <td className="col-date">{formatDate(result.date, result.time)}</td>
                  <td className="col-type">
                    <span className={`type-badge type-${result.type}`}>
                      {result.type.charAt(0).toUpperCase() + result.type.slice(1)}
                    </span>
                    {penaltyCounts[result.filename] > 0 && (
                      <span className="penalty-count-badge">
                        {penaltyCounts[result.filename]} penalt{penaltyCounts[result.filename] > 1 ? 'ies' : 'y'}
                      </span>
                    )}
                  </td>
                  <td className="col-size">{formatSize(result.size)}</td>
                  <td className="col-actions">
                    <button
                      className="view-button"
                      onClick={() => setViewingFile(result.filename)}
                      title="View results"
                    >
                      <Eye size={16} />
                      View
                    </button>
                    <button
                      className="download-button"
                      onClick={() => handleDownload(result.filename)}
                      disabled={downloading === result.filename}
                      title="Download result"
                    >
                      <Download size={16} />
                      {downloading === result.filename ? 'Downloading...' : 'Download'}
                    </button>
                    <button
                      className="delete-button"
                      onClick={() => handleDeleteResult(result.filename)}
                      title="Delete result"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewingFile && (
        <RaceResultsViewer
          serverId={serverId}
          filename={viewingFile}
          onClose={() => setViewingFile(null)}
        />
      )}
    </div>
  );
};

export default Results;
